import type { StatBlock } from './types';

export const formatNumber = (value: number) => value.toLocaleString('en-US');

/** "3h 12m" / "45s" — compact enough for an egg slot caption. */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return 'Ready!';
  const total = Math.ceil(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

export function timeAgo(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

export const titleCase = (slug: string) =>
  slug.split(/[-_ ]/).map((word) => (word ? word[0]!.toUpperCase() + word.slice(1) : '')).join(' ');

export const dexNumber = (id: number) => `#${String(id).padStart(3, '0')}`;

export const STAT_LABELS: Record<string, string> = {
  hp: 'HP',
  attack: 'Attack',
  defense: 'Defense',
  specialAttack: 'Sp. Atk',
  specialDefense: 'Sp. Def',
  speed: 'Speed',
};

/** Six stats at 31 each. Mirrors `MAX_IV_TOTAL` on the server. */
export const MAX_IV_TOTAL = 186;

/**
 * IVs as a percentage of perfect, which is how players actually talk about
 * them — "a 93% Dratini" says more at a glance than "173".
 *
 * The server sends `ivTotal` already summed, so this never re-adds the block.
 */
export const ivPercent = (pokemon: { ivTotal: number }) =>
  Math.round((pokemon.ivTotal / MAX_IV_TOTAL) * 100);

/** The per-stat detail, for a tooltip on the percentage. */
export const ivBreakdown = (pokemon: { ivs: StatBlock; ivTotal: number }) =>
  `${(Object.keys(STAT_LABELS) as Array<keyof StatBlock>)
    .map((key) => `${STAT_LABELS[key]} ${pokemon.ivs[key] ?? 0}`)
    .join(' · ')}\n${pokemon.ivTotal} / ${MAX_IV_TOTAL}`;

export const genderSymbol = (gender: string) =>
  gender === 'male' ? '♂' : gender === 'female' ? '♀' : '';

/**
 * Nidoran♀ and Nidoran♂ carry the symbol in the species name itself, so
 * appending the gender marker would render "Nidoran♀ ♀". Use this anywhere the
 * symbol sits next to a display name.
 */
export const genderSuffix = (displayName: string, gender: string) =>
  /[♀♂]\s*$/.test(displayName) ? '' : genderSymbol(gender);

export const ALL_TYPES = [
  'normal', 'fire', 'water', 'electric', 'grass', 'ice',
  'fighting', 'poison', 'ground', 'flying', 'psychic', 'bug',
  'rock', 'ghost', 'dragon', 'dark', 'steel', 'fairy',
];
